import React, { useState, useEffect } from 'react';
import { Outlet, Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useEmpresa } from '../contexts/EmpresaContext';
import { PeriodoProvider } from '../contexts/PeriodoContext';
import { Button } from '../components/ui/button';
import EmpresaSelector from '../components/EmpresaSelector';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Clock, LayoutDashboard, Settings, LogOut, User, 
  ChevronDown, Menu, X, Building2, Bell, FileBarChart, AlertTriangle, Users
} from 'lucide-react';

const EscritorioLayout = () => {
  const { user, logout, token } = useAuth();
  const { escritorio, empresaAtual, totalEmpresas, loading: empresaLoading } = useEmpresa();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const menuItems = [
    { icon: LayoutDashboard, label: 'Dashboard', path: '/escritorio' },
    { icon: Building2, label: 'Empresas', path: '/escritorio/empresas' },
    { icon: Users, label: 'Usuários', path: '/escritorio/usuarios' },
    { icon: AlertTriangle, label: 'Alertas', path: '/escritorio/alertas' },
    { icon: FileBarChart, label: 'Relatórios', path: '/escritorio/relatorios' },
    { icon: Settings, label: 'Configurações', path: '/escritorio/configuracoes' },
  ];

  const isActive = (path) => {
    if (path === '/escritorio') return location.pathname === path;
    return location.pathname.startsWith(path);
  };

  const renderMenuItem = (item) => (
    <Link
      key={item.path}
      to={item.path}
      data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
      className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 ${
        isActive(item.path)
          ? 'bg-accent text-white shadow-md'
          : 'text-slate-300 hover:bg-slate-700/50 hover:text-white'
      }`}
    >
      <item.icon className="w-5 h-5 flex-shrink-0" />
      <span className="truncate">{item.label}</span>
    </Link>
  );

  const content = (
    <div className="min-h-screen bg-slate-50">
      {/* Sidebar - Desktop */}
      <aside className="hidden lg:fixed lg:inset-y-0 lg:flex lg:w-64 lg:flex-col">
        <div className="flex flex-col flex-1 bg-primary">
          {/* Logo */}
          <div className="flex items-center gap-2 h-16 px-6 border-b border-slate-700 flex-shrink-0">
            <div className="w-8 h-8 bg-accent rounded-lg flex items-center justify-center">
              <Clock className="w-5 h-5 text-white" />
            </div>
            <span className="text-xl font-bold text-white font-['Outfit']">Jornyx</span>
          </div>

          {/* Escritório Info */}
          <div className="px-4 py-3 border-b border-slate-700">
            <div className="flex items-center gap-2 text-slate-300">
              <Building2 className="w-4 h-4 text-blue-400" />
              <span className="text-xs font-medium uppercase tracking-wider">Escritório</span>
            </div>
            <p className="text-sm text-white font-medium mt-1 truncate">
              {escritorio?.nome_fantasia || escritorio?.razao_social || 'Carregando...'}
            </p>
          </div>

          {/* Navigation */}
          <nav className="flex-1 py-4 px-3 overflow-y-auto">
            <div className="space-y-1">
              {menuItems.map(renderMenuItem)}
            </div>
          </nav>

          {/* User - fixo no fundo */}
          <div className="p-4 border-t border-slate-700 flex-shrink-0">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-slate-600 rounded-full flex items-center justify-center">
                <User className="w-5 h-5 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-white truncate">{user?.nome}</p>
                <p className="text-xs text-slate-400 truncate capitalize">{user?.perfil?.replace(/_/g, ' ')}</p>
              </div>
            </div>
          </div>
        </div>
      </aside>

      {/* Mobile sidebar */}
      {sidebarOpen && (
        <div className="lg:hidden fixed inset-0 z-50">
          <div className="fixed inset-0 bg-black/50" onClick={() => setSidebarOpen(false)} />
          <div className="fixed inset-y-0 left-0 w-64 bg-primary flex flex-col">
            <div className="flex items-center justify-between h-16 px-6 border-b border-slate-700 flex-shrink-0">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-accent rounded-lg flex items-center justify-center">
                  <Clock className="w-5 h-5 text-white" />
                </div>
                <span className="text-xl font-bold text-white font-['Outfit']">Jornyx</span>
              </div>
              <button onClick={() => setSidebarOpen(false)} className="text-white">
                <X className="w-6 h-6" />
              </button>
            </div>
            <nav className="flex-1 py-4 px-3 overflow-y-auto">
              <div className="space-y-1">
                {menuItems.map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setSidebarOpen(false)}
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                      isActive(item.path)
                        ? 'bg-accent text-white'
                        : 'text-slate-300 hover:bg-slate-700 hover:text-white'
                    }`}
                  >
                    <item.icon className="w-5 h-5" />
                    {item.label}
                  </Link>
                ))}
              </div>
            </nav>
          </div>
        </div>
      )}

      {/* Main content */}
      <div className="lg:pl-64 flex flex-col min-h-screen">
        {/* Top bar */}
        <header className="sticky top-0 z-40 bg-white shadow-sm">
          <div className="flex items-center justify-between h-16 px-4 lg:px-8">
            <div className="flex items-center gap-4">
              <button
                className="lg:hidden p-2 -ml-2 text-slate-600 hover:text-slate-900"
                onClick={() => setSidebarOpen(true)}
              >
                <Menu className="w-6 h-6" />
              </button>
              <h1 className="text-lg font-semibold text-slate-800 font-['Outfit']">
                Gestão de Clientes
              </h1>
            </div>

            <div className="flex items-center gap-4">
              {/* Seletor de Empresa */}
              <EmpresaSelector />
              
              {/* Info da empresa atual */}
              {empresaAtual && !empresaLoading && (
                <div className="hidden md:flex items-center gap-2 px-3 py-1.5 bg-blue-50 rounded-lg border border-blue-200">
                  <Building2 className="w-4 h-4 text-blue-600" />
                  <span className="text-sm text-blue-700 font-medium">
                    {empresaAtual.nome_fantasia || empresaAtual.razao_social}
                  </span>
                </div>
              )}

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="flex items-center gap-2">
                    <span className="hidden sm:inline text-sm">{user?.nome}</span>
                    <ChevronDown className="w-4 h-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel>Minha Conta</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate('/escritorio/configuracoes')}>
                    <Settings className="w-4 h-4 mr-2" />
                    Configurações
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="text-red-600">
                    <LogOut className="w-4 h-4 mr-2" />
                    Sair
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 p-4 lg:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );

  return (
    <PeriodoProvider token={token}>
      {content}
    </PeriodoProvider>
  );
};

export default EscritorioLayout;
